/**
 * 🩺 Phase 15.0 — Promo Health Panel
 *
 * Live dashboard showing operators the state of the promotional plays
 * engine: which mode is active (FIXED vs DYNAMIC), the effective
 * per-tier target + cap, scheduler counts (active / awaiting drip /
 * frozen / queued), and the timestamp of the most recent drip tick.
 *
 * Calls GET /api/admin/promo/health. Admin auth required. Read-only.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import CampaignTimelineModal from "@/components/admin/CampaignTimelineModal";

const RefreshIcon = ({ spinning }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={spinning ? "animate-spin" : ""}
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const Stat = ({ label, value, sub, tone = "amber", testid }) => {
  const palette = {
    amber: "border-amber-400/40 bg-amber-500/10 text-amber-100",
    emerald: "border-emerald-400/40 bg-emerald-500/10 text-emerald-100",
    rose: "border-rose-400/40 bg-rose-500/10 text-rose-100",
    purple: "border-purple-400/40 bg-purple-500/10 text-purple-100",
  }[tone];
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border px-3 py-2 ${palette}`}
    >
      <p className="text-[10px] uppercase tracking-wider font-black opacity-75">
        {label}
      </p>
      <p className="text-white font-black text-lg leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const PromoHealthPanel = () => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Phase 15.0.1 — Drip Now list state
  const [awaiting, setAwaiting] = useState([]);
  const [awaitingLoading, setAwaitingLoading] = useState(false);
  const [drippingId, setDrippingId] = useState(null);
  // Phase 15.0.2 — currently-open timeline modal target (sid).
  const [timelineSid, setTimelineSid] = useState(null);
  // Per-row local cooldown countdown (sid → secondsRemaining). Driven
  // off the server-provided cooldown_remaining_seconds and updated by
  // a single 1s interval shared across the list.
  const [cooldowns, setCooldowns] = useState({});
  const tickRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/promo/health");
      setSnap(r.data?.snapshot || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAwaiting = useCallback(async () => {
    setAwaitingLoading(true);
    try {
      const r = await api.get("/admin/promo/awaiting-drip?limit=25");
      const rows = r.data?.rows || [];
      setAwaiting(rows);
      // Seed cooldowns from the server response.
      const seed = {};
      for (const row of rows) {
        if (row.cooldown_remaining_seconds > 0) {
          seed[row.submission_id] = row.cooldown_remaining_seconds;
        }
      }
      setCooldowns(seed);
    } catch {
      // Non-fatal — keep the snapshot panel usable.
      setAwaiting([]);
    } finally {
      setAwaitingLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    refreshAwaiting();
  }, [refresh, refreshAwaiting]);

  // Local 1-second tick to count cooldowns down without re-fetching.
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setCooldowns((cur) => {
        let changed = false;
        const next = {};
        for (const k of Object.keys(cur)) {
          const v = (cur[k] || 0) - 1;
          if (v > 0) {
            next[k] = v;
            if (v !== cur[k]) changed = true;
          } else {
            changed = true;
          }
        }
        return changed ? next : cur;
      });
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  const dripNow = useCallback(async (sid, title) => {
    if (drippingId) return;
    setDrippingId(sid);
    try {
      const r = await api.post(`/admin/promo/drip-now/${encodeURIComponent(sid)}`);
      const res = r.data?.result || {};
      toast.success(
        `💧 Drip applied to "${title}"`,
        {
          description: `+${(res.delta || 0).toLocaleString()} plays · ` +
            `${(res.new_promotional_plays || 0).toLocaleString()}/${(res.promo_target || 0).toLocaleString()} (${res.progress_pct || 0}%)` +
            (res.frozen ? " · campaign now FROZEN" : ""),
          duration: 4500,
        },
      );
      // Local cooldown lock — match backend MANUAL_DRIP_COOLDOWN_SECONDS.
      setCooldowns((cur) => ({ ...cur, [sid]: 60 }));
      // Refresh both panels so counts + progress reflect the new state.
      refresh();
      refreshAwaiting();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const reason = detail?.reason || e?.message || "unknown";
      if (reason === "rate_limited") {
        const wait = detail?.retry_in_seconds || 60;
        setCooldowns((cur) => ({ ...cur, [sid]: wait }));
        toast.error(`Rate limited — retry in ${wait}s`);
      } else if (reason === "campaign_frozen") {
        toast.error("Campaign already frozen — nothing to drip");
        refreshAwaiting();
      } else if (reason === "target_already_reached") {
        toast.error("Target already reached");
        refreshAwaiting();
      } else if (reason === "not_found") {
        toast.error("Track not found");
        refreshAwaiting();
      } else {
        toast.error(`Drip failed: ${reason}`);
      }
    } finally {
      setDrippingId(null);
    }
  }, [drippingId, refresh, refreshAwaiting]);

  if (loading && !snap) {
    return (
      <div
        className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-gray-400 text-sm"
        data-testid="promo-health-panel-loading"
      >
        🩺 Loading promo health snapshot…
      </div>
    );
  }
  if (error && !snap) {
    return (
      <div
        className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-rose-200 text-sm"
        data-testid="promo-health-panel-error"
      >
        ⚠ Could not load Promo Health: {error}
      </div>
    );
  }
  if (!snap) return null;

  const tierRow = (tier) => {
    const t = snap.tiers?.[tier] || {};
    return (
      <tr key={tier} className="border-b border-amber-500/10 last:border-0">
        <td className="px-3 py-1.5 font-bold uppercase text-amber-200">{tier}</td>
        <td className="px-3 py-1.5 text-right text-emerald-300 font-black">
          {String(t.effective_target ?? "—")}
        </td>
        <td className="px-3 py-1.5 text-right">{t.amount?.toLocaleString() || 0}</td>
        <td className="px-3 py-1.5 text-right text-gray-400">
          {t.range_min?.toLocaleString() || 0}–{t.range_max?.toLocaleString() || 0}
        </td>
        <td className="px-3 py-1.5 text-right font-bold text-amber-100">
          {t.lifetime_cap?.toLocaleString() || 0}
        </td>
      </tr>
    );
  };

  const c = snap.counts || {};
  const lastDrip = snap.last_drip_log_at
    ? new Date(snap.last_drip_log_at).toLocaleString()
    : "—";
  const isFixed = snap.mode === "fixed";

  return (
    <div
      className="mb-6 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-pink-500/5 to-purple-500/5 p-4"
      data-testid="promo-health-panel"
    >
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-lg">🩺</span>
        <h4 className="text-white font-bold">Promo Health</h4>
        <span
          data-testid="promo-health-mode-pill"
          className={`ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-black border ${
            isFixed
              ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-200"
              : "bg-purple-500/15 border-purple-400/50 text-purple-200"
          }`}
        >
          Mode: {snap.mode}
        </span>
        <Button
          onClick={refresh}
          disabled={loading}
          variant="ghost"
          size="sm"
          className="ml-auto text-amber-200 hover:text-white hover:bg-amber-500/15"
          data-testid="promo-health-refresh"
        >
          <RefreshIcon spinning={loading} />
          <span className="ml-1.5 text-xs">{loading ? "Refreshing" : "Refresh"}</span>
        </Button>
      </div>
      <p className="text-xs text-gray-400 mb-4 max-w-3xl">
        Snapshot of the promo grant + drip scheduler. {isFixed ? (
          <>FIXED mode uses the exact <code className="bg-black/40 px-1 rounded">amount</code> column for every new feature.</>
        ) : (
          <>DYNAMIC mode picks a random target in <code className="bg-black/40 px-1 rounded">range_min–range_max</code> for every new feature.</>
        )}{" "}
        Global cap {snap.global_lifetime_cap?.toLocaleString()} · Surge window {snap.feature_duration_days}d ·
        Initial burst {Math.round((snap.initial_burst_fraction || 0) * 100)}%.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <Stat
          label="Active"
          value={c.active_promos ?? 0}
          sub="surge running"
          tone="emerald"
          testid="promo-health-count-active"
        />
        <Stat
          label="Awaiting drip"
          value={c.awaiting_drip ?? 0}
          sub="< target"
          tone="amber"
          testid="promo-health-count-awaiting-drip"
        />
        <Stat
          label="Frozen (cap)"
          value={c.frozen_by_cap ?? 0}
          sub="hit lifetime cap"
          tone="rose"
          testid="promo-health-count-frozen-cap"
        />
        <Stat
          label="Frozen (time)"
          value={c.frozen_by_time ?? 0}
          sub="surge window ended"
          tone="purple"
          testid="promo-health-count-frozen-time"
        />
        <Stat
          label="Queued"
          value={c.queued_eligible ?? 0}
          sub="featured, no target yet"
          tone="amber"
          testid="promo-health-count-queued"
        />
      </div>

      <div className="rounded-lg border border-amber-500/15 bg-black/30 overflow-x-auto">
        <table className="w-full text-xs text-left" data-testid="promo-health-tier-table">
          <thead className="text-gray-400 border-b border-amber-500/15">
            <tr>
              <th className="px-3 py-2">Tier</th>
              <th className="px-3 py-2 text-right">Effective Target</th>
              <th className="px-3 py-2 text-right">Amount (FIXED)</th>
              <th className="px-3 py-2 text-right">Range (DYNAMIC)</th>
              <th className="px-3 py-2 text-right">Lifetime Cap</th>
            </tr>
          </thead>
          <tbody className="text-gray-200">
            {tierRow("standard")}
            {tierRow("spotlight")}
            {tierRow("mega")}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center gap-3 text-[11px] text-gray-400">
        <span>
          Last drip:{" "}
          <span className="text-amber-200 font-semibold" data-testid="promo-health-last-drip">
            {lastDrip}
          </span>
        </span>
        <span className="text-gray-600">·</span>
        <span>Scheduler runs every 20s in production.</span>
      </div>

      {/* Phase 15.0.1 — Awaiting drip list with per-track Drip Now */}
      <div className="mt-4" data-testid="promo-health-awaiting-section">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-wider font-black text-amber-200">
            💧 Awaiting drip
          </span>
          <span className="text-[10px] text-gray-500">
            {awaiting.length} shown · {c.awaiting_drip ?? 0} total
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshAwaiting}
            disabled={awaitingLoading}
            className="ml-auto text-amber-200 hover:text-white hover:bg-amber-500/15 h-7 px-2"
            data-testid="promo-health-awaiting-refresh"
          >
            <RefreshIcon spinning={awaitingLoading} />
            <span className="ml-1.5 text-[11px]">Refresh list</span>
          </Button>
        </div>

        {awaiting.length === 0 ? (
          <div
            className="rounded-lg border border-amber-500/15 bg-black/20 px-3 py-4 text-center text-[11px] text-gray-500"
            data-testid="promo-health-awaiting-empty"
          >
            {awaitingLoading ? "Loading…" : "No active campaigns awaiting a drip right now."}
          </div>
        ) : (
          <div className="rounded-lg border border-amber-500/15 bg-black/30 divide-y divide-amber-500/10 overflow-hidden">
            {awaiting.map((row) => {
              const cd = cooldowns[row.submission_id] || 0;
              const isCoolingDown = cd > 0;
              const isMe = drippingId === row.submission_id;
              const disabled = isCoolingDown || isMe;
              return (
                <div
                  key={row.submission_id}
                  data-testid={`promo-awaiting-row-${row.submission_id}`}
                  className="px-3 py-2.5 flex items-center gap-3 hover:bg-amber-500/5 transition-colors cursor-pointer"
                  onClick={(ev) => {
                    // Don't open modal when the click is on the Drip Now button.
                    if (ev.target.closest("button")) return;
                    setTimelineSid(row.submission_id);
                  }}
                  title="Click to view campaign timeline"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-bold truncate" title={row.track_title}>
                        {row.track_title}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/30 text-amber-200">
                        {row.tier}
                      </span>
                      {row.status === "spotlight" && (
                        <span className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-400/30 text-purple-200">
                          ⭐ spotlight
                        </span>
                      )}
                      <span
                        className="ml-auto text-[10px] text-amber-300/70 hover:text-amber-200 font-bold"
                        data-testid={`promo-timeline-open-${row.submission_id}`}
                      >
                        Timeline →
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                      <span className="text-amber-300/80">{row.artist_name}</span>
                      <span className="text-gray-600 mx-1.5">·</span>
                      <span>
                        {row.promotional_plays?.toLocaleString()}/
                        {row.promo_target?.toLocaleString()} ({row.progress_pct}%)
                      </span>
                    </div>
                    <div className="mt-1 h-1 w-full rounded-full bg-black/50 overflow-hidden border border-white/5">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-pink-400 transition-[width] duration-500"
                        style={{ width: `${Math.max(0, Math.min(100, row.progress_pct || 0))}%` }}
                      />
                    </div>
                  </div>

                  <Button
                    size="sm"
                    disabled={disabled}
                    onClick={() => dripNow(row.submission_id, row.track_title)}
                    className={
                      isCoolingDown
                        ? "h-8 px-3 text-[11px] font-bold bg-gray-700 text-gray-300 hover:bg-gray-700 cursor-not-allowed"
                        : "h-8 px-3 text-[11px] font-bold bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white shadow-[0_0_14px_rgba(251,191,36,0.35)]"
                    }
                    data-testid={`promo-drip-now-btn-${row.submission_id}`}
                  >
                    {isMe ? (
                      <span>Dripping…</span>
                    ) : isCoolingDown ? (
                      <span>Cooldown {cd}s</span>
                    ) : (
                      <span>💧 Drip Now</span>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-2 text-[10px] text-gray-500 leading-snug">
          Manual drip respects every guardrail — lifetime caps, campaign
          expiration, and FIXED/DYNAMIC mode are NOT bypassed. 60s
          cooldown per track. Each click is audit-logged with your admin
          identity to <code className="bg-black/40 px-1 rounded">promotional_plays_log</code>{" "}
          (<code className="bg-black/40 px-1 rounded">reason=manual_drip</code>).
          Click any row to open the per-campaign Timeline.
        </p>
      </div>

      {/* Phase 15.0.2 — Per-campaign Activity Log modal */}
      <CampaignTimelineModal
        submissionId={timelineSid}
        open={!!timelineSid}
        onOpenChange={(o) => { if (!o) setTimelineSid(null); }}
      />
    </div>
  );
};

export default PromoHealthPanel;
